import Bugsnag from '@bugsnag/js'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Link,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { BsCheck } from 'react-icons/bs'
import { useNavigate } from 'react-router-dom'
import { client } from '../utils/client'
import { startDocusignSignaturePolling } from '../utils/docusignPolling'
import { FormDefaultState } from '../utils/constants'
import { isStepValid } from '../utils/misc'
import { FormType } from '../utils/sharedTypes'
import {
  isTiersDeclarantPlaceholder,
  resolveOpcoFundingReference,
  TIERS_DECLARANT_FUNDING_REFERENCE,
} from '../utils/tiersDeclarant'

type Props = {
  form: FormType
  setForm: Dispatch<SetStateAction<FormType>>
}

// First envelope creation can exceed the default axios timeout (PDF generation + DocuSign API).
const DOCUSIGN_ENVELOPE_TIMEOUT_MS = 60_000
const DOCUSIGN_LOAD_MAX_ATTEMPTS = 4
const DOCUSIGN_LOAD_RETRY_BASE_MS = 1500
const DOCUSIGN_LOADING_MESSAGE_INTERVAL_MS = 5000
const DOCUSIGN_LOADING_MESSAGES = [
  'Préparation de vos documents...',
  'Génération des documents PDF...',
  'Connexion à la plateforme de signature...',
  "Création de l'enveloppe électronique...",
  'Finalisation de votre dossier...',
  'Encore un instant, merci de patienter...',
]

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const Opco = ({ form, setForm }: Props) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isOpcoEp = form.data.funding_organism?.code === 'OPCO_EP'
  const isThirdPartyDeclarant = isOpcoEp && isTiersDeclarantPlaceholder(form.data.funding_reference)
  const hasRecordedOpcoDossier = Boolean(
    form.data.funding_reference && !isTiersDeclarantPlaceholder(form.data.funding_reference),
  )
  const isOpcoAlreadyProcessed =
    Boolean(form.data.opco_docusign?.signed_at || form.data.convention_signed_at) &&
    (hasRecordedOpcoDossier || Boolean(form.data.submitted) || form.data.opco_amount != null)
  const formatter = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  })
  const products = form.data.products.length > 0 ? form.data.products : form.data.product ? [form.data.product] : []

  const [docusignUrl, setDocusignUrl] = useState<string>('')
  const [isDocusignLoading, setIsDocusignLoading] = useState(true)
  const [docusignLoadFailed, setDocusignLoadFailed] = useState(false)
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
  const userOptedOutOfTiersDeclarant = useRef(false)
  const isSigned = Boolean(form.data.opco_docusign?.signed_at)

  // Finalize the DocuSign signature from the parent page. This is needed in local dev where the
  // embedded iframe cannot redirect to a localhost return URL (Private Network Access is blocked),
  // and acts as a robust fallback in production if the iframe postMessage is missed.
  const finalizeSignature = async (force = false): Promise<boolean> => {
    if (!form.data.global_id) return false
    const resp = await client.post(`/registration/${form.data.global_id}/sign_docusign/`, undefined, {
      params: force ? { force: '1' } : undefined,
    })
    const data = resp.data as { signed: boolean; url: string | null }
    if (data.signed) {
      setForm((prevState) => ({
        ...prevState,
        data: {
          ...prevState.data,
          opco_docusign: {
            envelope_id: prevState.data.opco_docusign?.envelope_id ?? null,
            sent_at: prevState.data.opco_docusign?.sent_at ?? null,
            signed_at: new Date().toISOString(),
            contract_url: data.url,
          },
          // Keep the historical *_signed_url fields in sync so the existing submit logic still
          // timestamps the signature server-side once the DocuSign bundle is signed.
          convention_signed_url: data.url,
          estimate_signed_url: data.url,
        },
      }))
    }
    return data.signed
  }

  const loadDocusignEnvelope = useCallback(async () => {
    if (!form.data.global_id || isSigned || form.data.funding_organism?.type !== 'opco') return

    setIsDocusignLoading(true)
    setDocusignLoadFailed(false)

    for (let attempt = 0; attempt < DOCUSIGN_LOAD_MAX_ATTEMPTS; attempt += 1) {
      try {
        const resp = await client.get(`/registration/${form.data.global_id}/get_or_create_docusign_envelope`, {
          timeout: DOCUSIGN_ENVELOPE_TIMEOUT_MS,
        })
        const url = (resp.data as string) || ''
        if (url) {
          setDocusignUrl(url)
          setIsDocusignLoading(false)
          return
        }
      } catch (err) {
        const isLastAttempt = attempt === DOCUSIGN_LOAD_MAX_ATTEMPTS - 1
        if (isLastAttempt) {
          Bugsnag.notify(err as Error)
          console.error(err)
        }
      }

      if (attempt < DOCUSIGN_LOAD_MAX_ATTEMPTS - 1) {
        await sleep(DOCUSIGN_LOAD_RETRY_BASE_MS * (attempt + 1))
      }
    }

    setDocusignUrl('')
    setDocusignLoadFailed(true)
    setIsDocusignLoading(false)
  }, [form.data.global_id, form.data.funding_organism?.type, isSigned])

  useEffect(() => {
    if (!isDocusignLoading) {
      setLoadingMessageIndex(0)
      return
    }
    const intervalId = setInterval(() => {
      setLoadingMessageIndex((prev) => Math.min(prev + 1, DOCUSIGN_LOADING_MESSAGES.length - 1))
    }, DOCUSIGN_LOADING_MESSAGE_INTERVAL_MS)
    return () => clearInterval(intervalId)
  }, [isDocusignLoading])

  useEffect(() => {
    if (isSigned) {
      setIsDocusignLoading(false)
      return
    }
    if (!form.data.global_id || form.data.funding_organism?.type !== 'opco') {
      // Registration or funding sync may still be in progress — keep the spinner instead of flashing an error.
      setIsDocusignLoading(true)
      setDocusignLoadFailed(false)
      return
    }
    loadDocusignEnvelope().catch(console.error)
  }, [form.data.global_id, form.data.funding_organism?.type, isSigned, loadDocusignEnvelope])

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data === 'DOCUSIGN_SIGNED_REFRESH') {
        finalizeSignature(true).catch(console.error)
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.data.global_id])

  // DocuSign sync while the iframe is open (PNA / missed postMessage fallback). Fast for 5 min, then slower.
  useEffect(() => {
    if (!form.data.global_id || isSigned || !docusignUrl) return

    let stopped = false
    const stopPolling = startDocusignSignaturePolling(() => {
      if (stopped) return
      return finalizeSignature().then((signed) => {
        if (signed) {
          stopped = true
          stopPolling()
        }
      })
    })

    const onVisibilityChange = () => {
      if (!document.hidden && !stopped) {
        finalizeSignature(true).catch(console.error)
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      stopped = true
      stopPolling()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.data.global_id, isSigned, docusignUrl])

  const submitFinancement = () => {
    const payload: Record<string, unknown> = { ...form.data }

    if (isTiersDeclarantPlaceholder(form.data.funding_reference) && isOpcoAlreadyProcessed) {
      delete payload.funding_reference
    }

    if (isSigned) {
      delete payload.convention_signed_url
      delete payload.estimate_signed_url
    }

    client
      .patch(`/registration/${form.data.global_id}/submit/`, payload)
      .then((resp) => {
        if (resp.status === 200) {
          setForm((prevState) => {
            return {
              ...prevState,
              data: {
                ...prevState.data,
                dpc_submitted: true,
              },
            }
          })

          if (
            form.data.custom_processes_validation &&
            form.data.pre_registration_custom_processes.some((p) => !p.validated)
          ) {
            navigate({ pathname: './../questionnaire/', search: location.search })
          } else {
            navigate({ pathname: './../recommandation/', search: location.search })
          }
        }
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
      })
  }

  const handleChange = (key: string, value: string | boolean | string[]) => {
    setForm((prevState) => {
      const errors = prevState.errors
      delete errors[key]
      return {
        ...prevState,
        data: {
          ...prevState.data,
          [key]: value,
        },
        errors: errors,
      }
    })
  }

  useEffect(() => {
    setForm((prevState) => {
      return {
        ...prevState,
        steps: prevState.steps.filter((step) => step.url !== 'recapitulatif'),
      }
    })

    return () => {
      setForm((prevState) => {
        return {
          ...prevState,
          steps: FormDefaultState.steps,
        }
      })
    }
  }, [])

  useEffect(() => {
    const current = form.data.funding_reference
    const next = resolveOpcoFundingReference({
      fundingOrganismCode: form.data.funding_organism?.code,
      fundingReference: current,
      userOptedOut: userOptedOutOfTiersDeclarant.current,
    })
    if ((next ?? '') !== (current ?? '')) {
      handleChange('funding_reference', next ?? '')
    }
    // Le défaut ne doit s'appliquer qu'au changement de financeur ou de référence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.data.funding_organism?.code, form.data.funding_reference])

  return (
    <Grid item container direction="column" spacing={1}>
      <Typography variant="h6">Vos documents...</Typography>
      <Grid item xs={12}>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="body1"
              align="center"
              sx={{
                p: 2,
                height: '100%',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
                boxShadow: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: '150px',
              }}
            >
              <Typography variant="subtitle1" fontWeight="medium" sx={{ flex: 1 }}>
                La convention de formation
              </Typography>
              <Link
                target="_blank"
                href={form.data?.convention_pdf_url || '#'}
                sx={{
                  textDecoration: 'none',
                  mt: 'auto',
                  pt: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  width: '100%',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Télécharger le document
              </Link>
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="body1"
              align="center"
              sx={{
                p: 2,
                height: '100%',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
                boxShadow: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: '150px',
              }}
            >
              <div>
                <Typography variant="subtitle1" fontWeight="medium" sx={{ flex: 1 }}>
                  Le devis
                </Typography>
                {form.data.estimate?.amount && (
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {formatter.format(form.data.estimate.amount)}
                  </Typography>
                )}
              </div>
              <Link
                target="_blank"
                href={form.data.estimate?.file_url || '#'}
                sx={{
                  textDecoration: 'none',
                  mt: 'auto',
                  pt: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  width: '100%',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Télécharger le document
              </Link>
            </Typography>
          </Grid>
          {products.map((product) => {
            return (
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body1"
                  align="center"
                  sx={{
                    p: 2,
                    height: '100%',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                    boxShadow: 1,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 3,
                      transform: 'translateY(-2px)',
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minHeight: '150px',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ flex: 1 }}>
                    Le programme détaillé
                  </Typography>
                  {products.length > 1 && (
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {product.name}
                    </Typography>
                  )}
                  <Link
                    target="_blank"
                    href={product.program_url}
                    sx={{
                      textDecoration: 'none',
                      mt: 'auto',
                      pt: 2,
                      borderTop: 1,
                      borderColor: 'divider',
                      width: '100%',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Télécharger le document
                  </Link>
                </Typography>
              </Grid>
            )
          })}
          <Grid item xs={12} sm={6}>
            <Typography
              variant="body1"
              align="center"
              sx={{
                p: 2,
                height: '100%',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
                boxShadow: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: '150px',
              }}
            >
              <Typography variant="subtitle1" fontWeight="medium" sx={{ flex: 1 }}>
                Le RIB de
                <br />
                Walter Learning
              </Typography>
              <Link
                href="https://walter-resources.s3.amazonaws.com/inscription/RIB Walter Learning (OPCO).pdf"
                target="_blank"
                sx={{
                  textDecoration: 'none',
                  mt: 'auto',
                  pt: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  width: '100%',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Télécharger le document
              </Link>
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item container sx={{ mt: 2 }}>
        <Typography variant="h6">... à signer électroniquement ci-dessous</Typography>
        <Grid item xs={12} sx={{ width: '100%', mt: 1 }}>
          {isSigned ? (
            <Grid container direction="column" alignItems="center" sx={{ p: 3 }}>
              <Grid
                item
                sx={{
                  background: theme.palette.success.main,
                  height: 64,
                  width: 64,
                  borderRadius: 32,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <BsCheck size="34px" style={{ color: 'white' }} />
              </Grid>
              <Typography>Vos documents ont été signés avec succès.</Typography>
            </Grid>
          ) : isDocusignLoading ? (
            <Grid container direction="column" alignItems="center" sx={{ p: 3 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2, textAlign: 'center' }}>
                {DOCUSIGN_LOADING_MESSAGES[loadingMessageIndex]}
              </Typography>
            </Grid>
          ) : docusignUrl ? (
            <Box sx={{ position: 'relative', width: '100%', height: { xs: 'calc(100vh - 320px)', sm: 600 } }}>
              <iframe
                src={docusignUrl}
                width="100%"
                height="100%"
                title="DocuSign Embedded Signing"
                style={{ border: 'none' }}
              />
            </Box>
          ) : docusignLoadFailed ? (
            <Grid container direction="column" alignItems="center" sx={{ p: 3 }}>
              <Typography sx={{ mb: 1, textAlign: 'center' }}>
                La signature électronique n'est pas disponible pour le moment.
              </Typography>
              <Button variant="outlined" onClick={() => loadDocusignEnvelope().catch(console.error)}>
                Réessayer
              </Button>
            </Grid>
          ) : (
            <Grid container direction="column" alignItems="center" sx={{ p: 3 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2, textAlign: 'center' }}>
                {DOCUSIGN_LOADING_MESSAGES[loadingMessageIndex]}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Grid item xs={12} sx={{ width: '100%', mt: 2 }}>
          <Typography variant="body1">
            Enfin, veuillez nous transmettre le numéro de dossier de votre demande de financement auprès de votre OPCO :
          </Typography>
          {isOpcoEp && !hasRecordedOpcoDossier && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={isThirdPartyDeclarant}
                  onChange={(_, checked) => {
                    userOptedOutOfTiersDeclarant.current = !checked
                    if (checked) {
                      handleChange('funding_reference', TIERS_DECLARANT_FUNDING_REFERENCE)
                    } else if (isTiersDeclarantPlaceholder(form.data.funding_reference)) {
                      handleChange('funding_reference', '')
                    }
                  }}
                />
              }
              label="Le dépôt du dossier sera effectué par Walter Learning en tant que tiers déclarant"
            />
          )}
          <Grid item xs={12} sx={{ width: '100%', mt: 1 }}>
            <TextField
              fullWidth
              label="Numéro OPCO"
              value={form.data.funding_reference || ''}
              onChange={(e) => handleChange('funding_reference', e.target.value)}
              disabled={(isOpcoEp && isThirdPartyDeclarant) || hasRecordedOpcoDossier}
            />
          </Grid>
        </Grid>
      </Grid>

      <Button
        variant="contained"
        onClick={submitFinancement}
        disabled={!isStepValid('financement', form)}
        sx={{
          height: {
            xs: 45,
          },
          mt: 2,
          ml: 'auto',
        }}
      >
        Valider
      </Button>
    </Grid>
  )
}

export default Opco
